import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import './ResourceGuide.css';

function GettingStarted() {
  return (
    <div className="guide-container">
      <div className="guide-header-section">
        <Link to="/resources" className="back-link">← Back to Resources</Link>
        <h1>Getting Started Guide</h1>
        <p className="guide-subtitle">Everything you need to launch your online presence and start winning customers</p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>Why Going Online Matters</h2>
          <p>
            Today's customers expect to find you online. Whether they're looking for your services, checking your hours, 
            or ready to buy, they'll search Google first. Having a strong online presence isn't optional anymore—it's essential.
          </p>
          <p>
            Businesses with a web presence grow faster, attract more customers, and keep them coming back with tools like 
            online ordering, loyalty programs, and direct communication.
          </p>
        </section>

        <section className="guide-section">
          <h2>Step 1: Set Up Your Website</h2>
          <div className="step-content">
            <div className="step-card">
              <h3>Your Digital Home Base</h3>
              <p>Your website is where customers learn about you, find your hours, see your offerings, and make their first purchase decision.</p>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Mobile-friendly design that works on phones and tablets</li>
                <li><CheckCircle size={20} /> Clear information about your business and offerings</li>
                <li><CheckCircle size={20} /> Easy contact options (phone, email, form)</li>
                <li><CheckCircle size={20} /> Professional photos and descriptions</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Step 2: Make It Easy to Find You</h2>
          <div className="step-content">
            <div className="step-card">
              <h3>SEO Basics (Search Engine Optimization)</h3>
              <p>Help customers find you when they search Google for what you offer.</p>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Claim your Google Business Profile (Free!)</li>
                <li><CheckCircle size={20} /> Use keywords customers actually search for</li>
                <li><CheckCircle size={20} /> Add your location and hours</li>
                <li><CheckCircle size={20} /> Collect customer reviews</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Step 3: Enable Online Ordering & Payments</h2>
          <div className="step-content">
            <div className="step-card">
              <h3>Open New Revenue Streams</h3>
              <p>Make it simple for customers to buy directly from you. Keep 100% of the sale instead of paying high commissions.</p>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Reduce dependency on third-party delivery apps</li>
                <li><CheckCircle size={20} /> Direct customer relationships and data</li>
                <li><CheckCircle size={20} /> Lower transaction fees</li>
                <li><CheckCircle size={20} /> Full control over pricing and promotions</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Step 4: Build Customer Loyalty</h2>
          <div className="step-content">
            <div className="step-card">
              <h3>Keep Customers Coming Back</h3>
              <p>Your first customer is worth acquiring, but your repeat customers are worth gold. Use loyalty programs and smart follow-ups.</p>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Loyalty rewards program (points, discounts)</li>
                <li><CheckCircle size={20} /> Email and SMS follow-ups for repeat orders</li>
                <li><CheckCircle size={20} /> Branded mobile app to stay top-of-mind</li>
                <li><CheckCircle size={20} /> Special offers for your best customers</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Quick Wins You Can Do Today</h2>
          <div className="quick-wins">
            <div className="win-card">
              <h3>Update Your Google Profile</h3>
              <p>Add hours, photos, and a description. Takes 20 minutes, huge impact.</p>
            </div>
            <div className="win-card">
              <h3>Ask for Reviews</h3>
              <p>Ask customers to leave reviews. Reviews build trust and boost Google rankings.</p>
            </div>
            <div className="win-card">
              <h3>Add a Call-to-Action</h3>
              <p>Make it crystal clear what you want customers to do: "Order Now", "Book Today", "Call Us".</p>
            </div>
          </div>
        </section>

        <section className="guide-cta">
          <h2>Ready to Get Started?</h2>
          <p>Learn how our platform makes all of this easy to manage in one place.</p>
          <Link to="/features" className="guide-cta-btn">See How It Works <ArrowRight size={18} /></Link>
        </section>
      </div>

      <div className="guide-navigation">
        <Link to="/resources/marketing-strategies" className="next-guide">
          Next: Marketing Strategies <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}

export default GettingStarted;
